import { useState, useEffect, useRef } from 'react';
import { API } from '../../config/api';
import { toFullUrl } from '../../config/api';
import { HomeFeatureStar } from './HomeFeatureStar';
import axios from 'axios';
import { toast } from 'sonner';
import { PreviewModeToggle } from './PreviewModeToggle';
import {
  UserPlus, Users, Check, X, Trash2, RefreshCw, Copy, Eye, EyeOff,
  Upload, Music, ExternalLink, Edit2, Star, StarOff, Globe, GlobeLock,
  Loader2, Play, Pause, ChevronDown, ChevronUp, Clock, CheckCircle, XCircle,
  Image as ImageIcon, Plus, Film, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const api = axios.create({ withCredentials: true });

export function FeaturedArtists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null); // null or artist obj
  const [createDialog, setCreateDialog] = useState(false);
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [artistTracks, setArtistTracks] = useState({});
  const [artistFreeMedia, setArtistFreeMedia] = useState({});
  const [playingTrack, setPlayingTrack] = useState(null);
  const audioRef = useRef(null);

  const fetchArtists = async () => {
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/all`);
      setArtists(data);
    } catch (err) {
      toast.error('Failed to load featured artists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArtists(); }, []);

  const fetchTracks = async (artistId) => {
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/${artistId}/tracks`);
      setArtistTracks(prev => ({ ...prev, [artistId]: data }));
    } catch (err) {
      toast.error('Failed to load tracks');
    }
  };

  const fetchFreeMedia = async (artistId) => {
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/${artistId}/free-media`);
      setArtistFreeMedia(prev => ({ ...prev, [artistId]: data }));
    } catch (err) {
      // Free-media is optional; silent fail
      setArtistFreeMedia(prev => ({ ...prev, [artistId]: [] }));
    }
  };

  const toggleExpand = (artistId) => {
    if (expandedArtist === artistId) {
      setExpandedArtist(null);
    } else {
      setExpandedArtist(artistId);
      if (!artistTracks[artistId]) fetchTracks(artistId);
      if (!artistFreeMedia[artistId]) fetchFreeMedia(artistId);
    }
  };

  const updateArtist = async (id, updates) => {
    try {
      await api.put(`${API}/featured-artists/admin/${id}`, updates);
      fetchArtists();
      toast.success('Updated');
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const deleteArtist = async (id) => {
    if (!window.confirm('Remove this artist and all their tracks?')) return;
    try {
      await api.delete(`${API}/featured-artists/admin/${id}`);
      fetchArtists();
      toast.success('Artist removed');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const regenerateCode = async (id) => {
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/${id}/regenerate-code`);
      fetchArtists();
      navigator.clipboard.writeText(data.access_code);
      toast.success('New code generated & copied!');
    } catch (err) {
      toast.error('Failed to regenerate code');
    }
  };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`${API}/featured-artists/admin/${id}/upload-photo`, fd);
      fetchArtists();
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const uploadCover = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/${id}/upload-cover`, fd);
      fetchArtists();
      toast.success('Cover image uploaded');
      return data;
    } catch (err) {
      toast.error('Cover upload failed');
    }
  };

  const uploadGalleryPhoto = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/featured-artists/admin/${id}/upload-gallery`, fd);
      fetchArtists();
      toast.success('Gallery photo added');
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const deleteGalleryPhoto = async (id, index) => {
    try {
      const { data } = await api.delete(`${API}/featured-artists/admin/${id}/gallery/${index}`);
      fetchArtists();
      toast.success('Photo removed');
      return data;
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const adminUploadTrack = async (artistId, { title, audio, cover }) => {
    if (!title?.trim() || !audio) { toast.error('Title and audio file required'); return false; }
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('audio', audio);
    if (cover) fd.append('cover', cover);
    try {
      await api.post(`${API}/featured-artists/admin/${artistId}/upload-track`, fd);
      await fetchTracks(artistId);
      fetchArtists();
      toast.success('Track uploaded & approved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Track upload failed');
      return false;
    }
  };

  const adminUploadFreeMedia = async (artistId, { title, media_type, file, thumbnail }) => {
    if (!title?.trim() || !file) { toast.error('Title and file required'); return false; }
    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('media_type', media_type || 'audio');
    fd.append('file', file);
    if (thumbnail) fd.append('thumbnail', thumbnail);
    try {
      await api.post(`${API}/featured-artists/admin/${artistId}/upload-free-media`, fd);
      await fetchFreeMedia(artistId);
      toast.success('Free media uploaded & approved');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      return false;
    }
  };

  const deleteFreeMedia = async (mediaId, artistId) => {
    if (!window.confirm('Delete this free media?')) return;
    try {
      await api.delete(`${API}/featured-artists/admin/free-media/${mediaId}`);
      fetchFreeMedia(artistId);
      toast.success('Free media deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const updateFeaturedTrack = async (trackId, payload, artistId) => {
    await api.put(`${API}/featured-artists/admin/tracks/${trackId}`, payload);
    fetchTracks(artistId);
  };

  const approveTrack = async (trackId, artistId) => {
    try {
      await api.put(`${API}/featured-artists/admin/tracks/${trackId}/approve`);
      fetchTracks(artistId);
      fetchArtists();
      toast.success('Track approved');
    } catch (err) {
      toast.error('Approval failed');
    }
  };

  const rejectTrack = async (trackId, artistId) => {
    try {
      await api.put(`${API}/featured-artists/admin/tracks/${trackId}/reject`);
      fetchTracks(artistId);
      fetchArtists();
      toast.success('Track rejected');
    } catch (err) {
      toast.error('Rejection failed');
    }
  };

  const deleteTrack = async (trackId, artistId) => {
    try {
      await api.delete(`${API}/featured-artists/admin/tracks/${trackId}`);
      fetchTracks(artistId);
      toast.success('Track deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const togglePlay = (trackUrl, trackId) => {
    if (playingTrack === trackId) {
      audioRef.current?.pause();
      setPlayingTrack(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(toFullUrl(trackUrl));
      audio.play();
      audio.onended = () => setPlayingTrack(null);
      audioRef.current = audio;
      setPlayingTrack(trackId);
    }
  };

  const statusColors = {
    approved: 'text-green-400 bg-green-400/10 border-green-400/30',
    pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    rejected: 'text-red-400 bg-red-400/10 border-red-400/30',
  };

  const statusIcons = {
    approved: CheckCircle,
    pending: Clock,
    rejected: XCircle,
  };

  const pending = artists.filter(a => a.status === 'pending');
  const approved = artists.filter(a => a.status === 'approved');
  const rejected = artists.filter(a => a.status === 'rejected');

  return (
    <div className="space-y-6" data-testid="featured-artists-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">FEATURED ARTISTS</h2>
          <p className="text-[#A3A3A3] text-sm mt-1">
            {artists.length} artist{artists.length !== 1 ? 's' : ''} — {pending.length} pending approval
          </p>
        </div>
        <Button onClick={() => setCreateDialog(true)} className="rounded-none bg-[#DAA520] hover:bg-[#FFD700] text-black font-bold" data-testid="add-artist-btn">
          <UserPlus className="w-4 h-4 mr-2" /> ADD ARTIST
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#DAA520]" />
        </div>
      ) : artists.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Users className="w-10 h-10 mx-auto text-[#333] mb-3" />
          <p className="text-[#666]">No featured artists yet. Add one to get started!</p>
        </div>
      ) : (
        <>
          {/* Pending Requests */}
          {pending.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-400 flex items-center gap-2">
                <Clock className="w-4 h-4" /> PENDING REQUESTS ({pending.length})
              </h3>
              {pending.map(a => (
                <ArtistCard key={a.id} artist={a} statusColors={statusColors} statusIcons={statusIcons}
                  onApprove={() => updateArtist(a.id, { status: 'approved' })}
                  onReject={() => updateArtist(a.id, { status: 'rejected' })}
                  onDelete={() => deleteArtist(a.id)}
                  onEdit={() => setEditDialog(a)}
                  expanded={expandedArtist === a.id}
                  onToggle={() => toggleExpand(a.id)}
                  tracks={artistTracks[a.id] || []}
                  freeMedia={artistFreeMedia[a.id] || []}
                  onApproveTrack={(tid) => approveTrack(tid, a.id)}
                  onRejectTrack={(tid) => rejectTrack(tid, a.id)}
                  onDeleteTrack={(tid) => deleteTrack(tid, a.id)}
                  onUpdateTrack={(tid, payload) => updateFeaturedTrack(tid, payload, a.id)}
                  onUploadPhoto={(file) => uploadPhoto(a.id, file)}
                  onUploadTrack={(payload) => adminUploadTrack(a.id, payload)}
                  onUploadFreeMedia={(payload) => adminUploadFreeMedia(a.id, payload)}
                  onDeleteFreeMedia={(mid) => deleteFreeMedia(mid, a.id)}
                  onRegenerateCode={() => regenerateCode(a.id)}
                  onCopyCode={() => copyCode(a.access_code)}
                  onToggleHomepage={() => updateArtist(a.id, { show_on_homepage: !a.show_on_homepage })}
                  onToggleProfile={() => updateArtist(a.id, { has_profile_page: !a.has_profile_page })}
                  playingTrack={playingTrack}
                  onTogglePlay={togglePlay}
                />
              ))}
            </div>
          )}

          {/* Approved */}
          {approved.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-green-400 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> APPROVED ({approved.length})
              </h3>
              {approved.map(a => (
                <ArtistCard key={a.id} artist={a} statusColors={statusColors} statusIcons={statusIcons}
                  onDelete={() => deleteArtist(a.id)}
                  onEdit={() => setEditDialog(a)}
                  expanded={expandedArtist === a.id}
                  onToggle={() => toggleExpand(a.id)}
                  tracks={artistTracks[a.id] || []}
                  freeMedia={artistFreeMedia[a.id] || []}
                  onApproveTrack={(tid) => approveTrack(tid, a.id)}
                  onRejectTrack={(tid) => rejectTrack(tid, a.id)}
                  onDeleteTrack={(tid) => deleteTrack(tid, a.id)}
                  onUpdateTrack={(tid, payload) => updateFeaturedTrack(tid, payload, a.id)}
                  onUploadPhoto={(file) => uploadPhoto(a.id, file)}
                  onUploadTrack={(payload) => adminUploadTrack(a.id, payload)}
                  onUploadFreeMedia={(payload) => adminUploadFreeMedia(a.id, payload)}
                  onDeleteFreeMedia={(mid) => deleteFreeMedia(mid, a.id)}
                  onRegenerateCode={() => regenerateCode(a.id)}
                  onCopyCode={() => copyCode(a.access_code)}
                  onToggleHomepage={() => updateArtist(a.id, { show_on_homepage: !a.show_on_homepage })}
                  onToggleProfile={() => updateArtist(a.id, { has_profile_page: !a.has_profile_page })}
                  playingTrack={playingTrack}
                  onTogglePlay={togglePlay}
                />
              ))}
            </div>
          )}

          {/* Rejected */}
          {rejected.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-red-400 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> REJECTED ({rejected.length})
              </h3>
              {rejected.map(a => (
                <ArtistCard key={a.id} artist={a} statusColors={statusColors} statusIcons={statusIcons}
                  onApprove={() => updateArtist(a.id, { status: 'approved' })}
                  onDelete={() => deleteArtist(a.id)}
                  onEdit={() => setEditDialog(a)}
                  expanded={expandedArtist === a.id}
                  onToggle={() => toggleExpand(a.id)}
                  tracks={artistTracks[a.id] || []}
                  freeMedia={artistFreeMedia[a.id] || []}
                  onApproveTrack={(tid) => approveTrack(tid, a.id)}
                  onRejectTrack={(tid) => rejectTrack(tid, a.id)}
                  onDeleteTrack={(tid) => deleteTrack(tid, a.id)}
                  onUpdateTrack={(tid, payload) => updateFeaturedTrack(tid, payload, a.id)}
                  onUploadPhoto={(file) => uploadPhoto(a.id, file)}
                  onUploadTrack={(payload) => adminUploadTrack(a.id, payload)}
                  onUploadFreeMedia={(payload) => adminUploadFreeMedia(a.id, payload)}
                  onDeleteFreeMedia={(mid) => deleteFreeMedia(mid, a.id)}
                  onRegenerateCode={() => regenerateCode(a.id)}
                  onCopyCode={() => copyCode(a.access_code)}
                  onToggleHomepage={() => updateArtist(a.id, { show_on_homepage: !a.show_on_homepage })}
                  onToggleProfile={() => updateArtist(a.id, { has_profile_page: !a.has_profile_page })}
                  playingTrack={playingTrack}
                  onTogglePlay={togglePlay}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <CreateArtistDialog open={createDialog} onClose={() => setCreateDialog(false)} onCreated={fetchArtists} />

      {/* Edit Dialog */}
      {editDialog && (
        <EditArtistDialog
          artist={editDialog}
          onClose={() => setEditDialog(null)}
          onUpdated={fetchArtists}
          onUploadPhoto={(file) => uploadPhoto(editDialog.id, file)}
          onUploadCover={(file) => uploadCover(editDialog.id, file)}
          onUploadGallery={(file) => uploadGalleryPhoto(editDialog.id, file)}
          onDeleteGallery={(idx) => deleteGalleryPhoto(editDialog.id, idx)}
        />
      )}
    </div>
  );
}


// ── Artist Card ──
function ArtistCard({ artist, statusColors, statusIcons, onApprove, onReject, onDelete, onEdit,
  expanded, onToggle, tracks, freeMedia, onApproveTrack, onRejectTrack, onDeleteTrack, onUpdateTrack,
  onUploadPhoto, onUploadTrack, onUploadFreeMedia, onDeleteFreeMedia,
  onRegenerateCode, onCopyCode, onToggleHomepage, onToggleProfile,
  playingTrack, onTogglePlay }) {

  const photoInputRef = useRef(null);
  const [showTrackUpload, setShowTrackUpload] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const StatusIcon = statusIcons[artist.status] || Clock;

  return (
    <div className="bg-[#141414] border border-[#262626] overflow-hidden" data-testid={`artist-card-${artist.id}`}>
      <div className="flex items-start gap-4 p-4">
        {/* Photo */}
        <div className="relative w-16 h-16 bg-[#262626] flex-shrink-0 overflow-hidden group cursor-pointer"
          onClick={() => photoInputRef.current?.click()}>
          {artist.photo ? (
            <img src={toFullUrl(artist.photo)} alt={artist.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#666]">
              <Users className="w-6 h-6" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Upload className="w-4 h-4 text-white" />
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && onUploadPhoto(e.target.files[0])} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-cabinet font-bold text-lg uppercase truncate">{artist.name}</h3>
            <HomeFeatureStar item={artist} endpoint="featured-artists" />
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${statusColors[artist.status]}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />{artist.status}
            </span>
            {artist.pending_tracks > 0 && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 border border-[#DAA520]/30 text-[#DAA520] bg-[#DAA520]/10">
                {artist.pending_tracks} PENDING TRACK{artist.pending_tracks !== 1 ? 'S' : ''}
              </span>
            )}
          </div>
          {artist.email && <p className="text-xs text-[#666] mt-0.5">{artist.email}</p>}
          {artist.bio && <p className="text-xs text-[#A3A3A3] mt-1 line-clamp-2">{artist.bio}</p>}

          {/* Toggle buttons */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button onClick={onToggleHomepage}
              className={`flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 border transition-colors ${artist.show_on_homepage ? 'border-[#DAA520] text-[#DAA520] bg-[#DAA520]/10' : 'border-[#333] text-[#666]'}`}
              data-testid={`toggle-homepage-${artist.id}`}>
              {artist.show_on_homepage ? <Star className="w-3 h-3" /> : <StarOff className="w-3 h-3" />}
              HOMEPAGE
            </button>
            <button onClick={onToggleProfile}
              className={`flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 border transition-colors ${artist.has_profile_page ? 'border-green-500 text-green-400 bg-green-400/10' : 'border-[#333] text-[#666]'}`}
              data-testid={`toggle-profile-${artist.id}`}>
              {artist.has_profile_page ? <Globe className="w-3 h-3" /> : <GlobeLock className="w-3 h-3" />}
              PROFILE PAGE
            </button>
            {artist.access_code && (
              <button onClick={onCopyCode}
                className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 border border-[#333] text-[#666] hover:text-white hover:border-[#555] transition-colors"
                data-testid={`copy-code-${artist.id}`}>
                <Copy className="w-3 h-3" /> CODE
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {artist.status === 'pending' && onApprove && (
            <button onClick={onApprove} className="w-8 h-8 flex items-center justify-center text-green-400 hover:bg-green-400/10 transition-colors" data-testid={`approve-${artist.id}`}>
              <Check className="w-4 h-4" />
            </button>
          )}
          {artist.status === 'pending' && onReject && (
            <button onClick={onReject} className="w-8 h-8 flex items-center justify-center text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`reject-${artist.id}`}>
              <X className="w-4 h-4" />
            </button>
          )}
          {artist.status === 'rejected' && onApprove && (
            <button onClick={onApprove} className="w-8 h-8 flex items-center justify-center text-green-400 hover:bg-green-400/10 transition-colors" data-testid={`approve-${artist.id}`}>
              <Check className="w-4 h-4" />
            </button>
          )}
          <button onClick={onEdit} className="px-3 py-1.5 text-xs font-bold uppercase bg-[#DAA520] hover:bg-[#FFD700] text-black flex items-center gap-1 transition-colors" data-testid={`edit-${artist.id}`}>
            <Edit2 className="w-3.5 h-3.5" /> EDIT
          </button>
          <button onClick={onRegenerateCode} className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-[#DAA520] transition-colors" title="Regenerate access code" data-testid={`regen-code-${artist.id}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-red-500 transition-colors" data-testid={`delete-${artist.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={onToggle} className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-white transition-colors" data-testid={`expand-${artist.id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded: Tracks + Free Media */}
      {expanded && (
        <div className="border-t border-[#262626] p-4 bg-[#0A0A0A] space-y-4" data-testid={`tracks-panel-${artist.id}`}>
          {/* Tracks header + upload */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#DAA520]">
                <Music className="w-3.5 h-3.5 inline mr-1" /> TRACKS ({tracks.length})
              </h4>
              {onUploadTrack && (
                <button onClick={() => setShowTrackUpload(true)}
                  className="text-[10px] font-bold uppercase px-2 py-1 border border-[#262626] text-[#DAA520] hover:bg-[#DAA520]/10 transition-colors flex items-center gap-1"
                  data-testid={`add-track-${artist.id}`}>
                  <Plus className="w-3 h-3" /> ADD TRACK
                </button>
              )}
            </div>
            {tracks.length === 0 ? (
              <p className="text-xs text-[#666] py-3 text-center">No tracks uploaded yet.</p>
            ) : (
              <div className="space-y-2">
                {tracks.map(track => (
                  <div key={track.id} className="flex items-center gap-3 bg-[#141414] border border-[#262626] p-2">
                    <button onClick={() => onTogglePlay(track.audio_url, track.id)}
                      className="w-8 h-8 flex items-center justify-center bg-[#262626] hover:bg-[#333] transition-colors flex-shrink-0"
                      data-testid={`play-track-${track.id}`}>
                      {playingTrack === track.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    {track.cover_url ? (
                      <img src={toFullUrl(track.cover_url)} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 bg-[#262626] flex items-center justify-center flex-shrink-0">
                        <Music className="w-3 h-3 text-[#666]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{track.title}</p>
                      <span className={`text-[10px] font-bold uppercase ${statusColors[track.status]?.split(' ')[0]}`}>{track.status}</span>
                    </div>
                    {onUpdateTrack && (
                      <div className="flex-shrink-0">
                        <PreviewModeToggle
                          value={{ preview_mode: track.preview_mode || 'full', unlock_price: track.unlock_price || 0 }}
                          onChange={(payload) => onUpdateTrack(track.id, payload)}
                          testid={`ftrack-${track.id}`}
                        />
                      </div>
                    )}
                    <div className="flex gap-1">
                      {track.status === 'pending' && (
                        <>
                          <button onClick={() => onApproveTrack(track.id)} className="w-7 h-7 flex items-center justify-center text-green-400 hover:bg-green-400/10" data-testid={`approve-track-${track.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => onRejectTrack(track.id)} className="w-7 h-7 flex items-center justify-center text-red-400 hover:bg-red-400/10" data-testid={`reject-track-${track.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button onClick={() => onDeleteTrack(track.id)} className="w-7 h-7 flex items-center justify-center text-[#666] hover:text-red-500" data-testid={`delete-track-${track.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Free Media header + upload */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#22C55E]">
                <Download className="w-3.5 h-3.5 inline mr-1" /> FREE MEDIA ({(freeMedia || []).length})
              </h4>
              {onUploadFreeMedia && (
                <button onClick={() => setShowMediaUpload(true)}
                  className="text-[10px] font-bold uppercase px-2 py-1 border border-[#262626] text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors flex items-center gap-1"
                  data-testid={`add-free-media-${artist.id}`}>
                  <Plus className="w-3 h-3" /> ADD FREE MEDIA
                </button>
              )}
            </div>
            {(!freeMedia || freeMedia.length === 0) ? (
              <p className="text-xs text-[#666] py-3 text-center">No free media yet.</p>
            ) : (
              <div className="space-y-2">
                {freeMedia.map(m => {
                  const IconType = m.media_type === 'video' ? Film : (m.media_type === 'image' ? ImageIcon : Music);
                  return (
                    <div key={m.id} className="flex items-center gap-3 bg-[#141414] border border-[#262626] p-2" data-testid={`admin-free-media-${m.id}`}>
                      {m.thumbnail_url ? (
                        <img src={toFullUrl(m.thumbnail_url)} alt="" className="w-8 h-8 object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 bg-[#262626] flex items-center justify-center flex-shrink-0">
                          <IconType className="w-3 h-3 text-[#666]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{m.title}</p>
                        <span className={`text-[10px] font-bold uppercase ${statusColors[m.status]?.split(' ')[0]}`}>{m.media_type} · {m.status}</span>
                      </div>
                      <a href={toFullUrl(m.file_url)} target="_blank" rel="noreferrer"
                        className="w-7 h-7 flex items-center justify-center text-[#A3A3A3] hover:text-white" title="Open">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {onDeleteFreeMedia && (
                        <button onClick={() => onDeleteFreeMedia(m.id)} className="w-7 h-7 flex items-center justify-center text-[#666] hover:text-red-500" data-testid={`delete-free-media-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inline track upload dialog */}
      {showTrackUpload && onUploadTrack && (
        <UploadTrackDialog
          onClose={() => setShowTrackUpload(false)}
          onSubmit={async (payload) => {
            const ok = await onUploadTrack(payload);
            if (ok) setShowTrackUpload(false);
          }}
        />
      )}

      {/* Inline free media upload dialog */}
      {showMediaUpload && onUploadFreeMedia && (
        <UploadFreeMediaDialog
          onClose={() => setShowMediaUpload(false)}
          onSubmit={async (payload) => {
            const ok = await onUploadFreeMedia(payload);
            if (ok) setShowMediaUpload(false);
          }}
        />
      )}
    </div>
  );
}


// ── Upload Track Dialog ──
function UploadTrackDialog({ onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    if (!audio) { toast.error('Audio file required'); return; }
    setSaving(true);
    await onSubmit({ title, audio, cover });
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md" data-testid="upload-track-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase">ADD TRACK</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="upload-track-title" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Audio File * (mp3/wav)</Label>
            <Input type="file" accept="audio/*" onChange={e => setAudio(e.target.files?.[0] || null)}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs" data-testid="upload-track-audio" />
            {audio && <p className="text-[10px] text-[#666] mt-1 truncate">{audio.name}</p>}
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3] font-bold uppercase">Cover Art (optional)</Label>
            <Input type="file" accept="image/*" onChange={e => setCover(e.target.files?.[0] || null)}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs" data-testid="upload-track-cover" />
            {cover && <p className="text-[10px] text-[#666] mt-1 truncate">{cover.name}</p>}
            <p className="text-[10px] text-[#525252] mt-1">Recommended: 1500×1500px square JPG/PNG</p>
          </div>
          <Button onClick={handle} disabled={saving} className="w-full rounded-none bg-[#DAA520] text-black font-bold" data-testid="upload-track-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            UPLOAD & APPROVE
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Upload Free Media Dialog ──
function UploadFreeMediaDialog({ onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [mediaType, setMediaType] = useState('audio');
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    if (!file) { toast.error('File required'); return; }
    setSaving(true);
    await onSubmit({ title, media_type: mediaType, file, thumbnail });
    setSaving(false);
  };

  const acceptByType = mediaType === 'video' ? 'video/*' : mediaType === 'image' ? 'image/*' : 'audio/*';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md" data-testid="upload-free-media-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase">ADD FREE MEDIA</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs text-[#22C55E] font-bold uppercase">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="upload-fm-title" />
          </div>
          <div>
            <Label className="text-xs text-[#22C55E] font-bold uppercase">Media Type</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {['audio', 'video', 'image'].map(t => (
                <button key={t} onClick={() => { setMediaType(t); setFile(null); }}
                  className={`text-[10px] font-bold uppercase py-2 border transition-colors ${mediaType === t ? 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/10' : 'border-[#262626] text-[#666] hover:text-white'}`}
                  data-testid={`fm-type-${t}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#22C55E] font-bold uppercase">File *</Label>
            <Input type="file" accept={acceptByType} onChange={e => setFile(e.target.files?.[0] || null)}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs" data-testid="upload-fm-file" />
            {file && <p className="text-[10px] text-[#666] mt-1 truncate">{file.name}</p>}
          </div>
          {mediaType !== 'image' && (
            <div>
              <Label className="text-xs text-[#A3A3A3] font-bold uppercase">Thumbnail (optional)</Label>
              <Input type="file" accept="image/*" onChange={e => setThumbnail(e.target.files?.[0] || null)}
                className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none text-xs" data-testid="upload-fm-thumb" />
              {thumbnail && <p className="text-[10px] text-[#666] mt-1 truncate">{thumbnail.name}</p>}
            </div>
          )}
          <Button onClick={handle} disabled={saving} className="w-full rounded-none bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold" data-testid="upload-fm-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            UPLOAD & APPROVE
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Create Dialog ──
function CreateArtistDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', bio: '', links: {} });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post(`${API}/featured-artists/admin/create`, form);
      toast.success('Artist added!');
      onCreated();
      onClose();
      setForm({ name: '', email: '', bio: '', links: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase">ADD FEATURED ARTIST</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Artist Name" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="create-name" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Email</Label>
            <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="artist@email.com" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="create-email" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Bio</Label>
            <Textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
              placeholder="Short bio..." rows={3} className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="create-bio" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-[#666] uppercase">Spotify</Label>
              <Input value={form.links.spotify || ''} onChange={e => setForm(p => ({ ...p, links: { ...p.links, spotify: e.target.value } }))}
                placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] text-[#666] uppercase">Instagram</Label>
              <Input value={form.links.instagram || ''} onChange={e => setForm(p => ({ ...p, links: { ...p.links, instagram: e.target.value } }))}
                placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] text-[#666] uppercase">SoundCloud</Label>
              <Input value={form.links.soundcloud || ''} onChange={e => setForm(p => ({ ...p, links: { ...p.links, soundcloud: e.target.value } }))}
                placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] text-[#666] uppercase">Website</Label>
              <Input value={form.links.website || ''} onChange={e => setForm(p => ({ ...p, links: { ...p.links, website: e.target.value } }))}
                placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="w-full rounded-none bg-[#DAA520] text-black font-bold" data-testid="create-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            ADD ARTIST
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Edit Dialog ──
function EditArtistDialog({ artist, onClose, onUpdated, onUploadPhoto, onUploadCover, onUploadGallery, onDeleteGallery }) {
  const [form, setForm] = useState({
    name: artist.name || '',
    email: artist.email || '',
    bio: artist.bio || '',
    slug: artist.slug || '',
    links: artist.links || {},
    order: artist.order || 999,
    videos: Array.isArray(artist.videos) ? [...artist.videos] : [],
    show_free_downloads: artist.show_free_downloads !== false,
    preview_mode: artist.preview_mode === 'snippet' ? 'snippet' : 'full',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(artist.photo || '');
  const [coverUrl, setCoverUrl] = useState(artist.cover_image || '');
  const [gallery, setGallery] = useState(Array.isArray(artist.gallery) ? artist.gallery : []);
  const photoRef = useRef(null);
  const coverRef = useRef(null);
  const galleryRef = useRef(null);

  const handlePhotoChange = async (file) => {
    if (!file || !onUploadPhoto) return;
    setUploading(true);
    try {
      const result = await onUploadPhoto(file);
      const objectUrl = URL.createObjectURL(file);
      setPhotoUrl(objectUrl);
      if (result?.photo) setPhotoUrl(result.photo);
    } catch (_e) {
      // toast handled by uploader
    } finally {
      setUploading(false);
    }
  };

  const handleCoverChange = async (file) => {
    if (!file || !onUploadCover) return;
    setUploadingCover(true);
    try {
      const result = await onUploadCover(file);
      const objectUrl = URL.createObjectURL(file);
      setCoverUrl(objectUrl);
      if (result?.cover_image) setCoverUrl(result.cover_image);
    } catch (_e) {
      // toast handled by uploader
    } finally {
      setUploadingCover(false);
    }
  };

  const handleGalleryAdd = async (file) => {
    if (!file || !onUploadGallery) return;
    if (gallery.length >= 8) { toast.error('Gallery is full (max 8 photos)'); return; }
    setUploadingGallery(true);
    try {
      const result = await onUploadGallery(file);
      if (result?.gallery) setGallery(result.gallery);
    } catch (_e) {
      // toast handled by uploader
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleGalleryRemove = async (idx) => {
    if (!onDeleteGallery) return;
    const result = await onDeleteGallery(idx);
    if (result?.gallery) setGallery(result.gallery);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.put(`${API}/featured-artists/admin/${artist.id}`, form);
      toast.success('Updated!');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase">EDIT ARTIST</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Photo */}
          <div className="flex items-center gap-3">
            <div className="relative w-20 h-20 bg-[#0A0A0A] border border-[#262626] overflow-hidden flex-shrink-0">
              {photoUrl ? (
                <img src={toFullUrl(photoUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#666]">
                  <Users className="w-8 h-8" />
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <Label className="text-xs text-[#DAA520] font-bold uppercase">Photo</Label>
              <input ref={photoRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && handlePhotoChange(e.target.files[0])} />
              <Button onClick={() => photoRef.current?.click()} disabled={uploading || !onUploadPhoto}
                className="mt-1 rounded-none bg-[#262626] hover:bg-[#333] text-white text-xs font-bold uppercase h-9 w-full"
                data-testid="edit-photo-upload">
                <Upload className="w-3.5 h-3.5 mr-2" /> {photoUrl ? 'Change Photo' : 'Upload Photo'}
              </Button>
              <p className="text-[10px] text-[#525252] mt-1">Square avatar · 500×500px recommended</p>
            </div>
          </div>

          {/* Cover / Banner Image */}
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Cover / Banner Image</Label>
            <p className="text-[10px] text-[#666] mt-0.5 mb-2">Wide hero banner shown at the top of /artist/{form.slug} page.</p>
            <div className="relative w-full h-24 bg-[#0A0A0A] border border-[#262626] overflow-hidden">
              {coverUrl ? (
                <img src={toFullUrl(coverUrl)} alt="cover" className="w-full h-full object-cover" data-testid="edit-cover-preview" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#444]">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
              {uploadingCover && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                </div>
              )}
            </div>
            <input ref={coverRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => e.target.files?.[0] && handleCoverChange(e.target.files[0])} />
            <Button onClick={() => coverRef.current?.click()} disabled={uploadingCover || !onUploadCover}
              className="mt-2 rounded-none bg-[#262626] hover:bg-[#333] text-white text-xs font-bold uppercase h-9 w-full"
              data-testid="edit-cover-upload">
              <Upload className="w-3.5 h-3.5 mr-2" /> {coverUrl ? 'Change Cover' : 'Upload Cover'}
            </Button>
            <p className="text-[10px] text-[#525252] mt-1">Recommended: 1920×640px (3:1 wide) · JPG/PNG/WebP</p>
          </div>

          {/* Gallery (up to 3 photos for sub-page) */}
          <div data-testid="gallery-section">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-[#DAA520] font-bold uppercase">Gallery ({gallery.length} / 8)</Label>
              {gallery.length < 8 && onUploadGallery && (
                <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploadingGallery}
                  className="text-[10px] font-bold uppercase px-2 py-1 border border-[#262626] text-[#DAA520] hover:bg-[#DAA520]/10 transition-colors flex items-center gap-1 disabled:opacity-50"
                  data-testid="add-gallery-photo">
                  {uploadingGallery ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} ADD PHOTO
                </button>
              )}
            </div>
            <p className="text-[10px] text-[#666] mt-0.5 mb-2">Up to 3 additional photos shown on the artist&apos;s /artist/{form.slug} sub-page.</p>
            <input ref={galleryRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => e.target.files?.[0] && handleGalleryAdd(e.target.files[0])} />
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map(i => {
                const url = gallery[i];
                return (
                  <div key={i} className="relative aspect-square bg-[#0A0A0A] border border-[#262626] overflow-hidden group" data-testid={`gallery-slot-${i}`}>
                    {url ? (
                      <>
                        <img src={toFullUrl(url)} alt={`gallery ${i + 1}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => handleGalleryRemove(i)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-600/90 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove photo"
                          data-testid={`gallery-remove-${i}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#333]">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#525252] mt-1">Square (1:1) recommended · 800×800px+</p>
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="edit-name" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Slug (URL)</Label>
            <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="edit-slug" />
            <p className="text-[10px] text-[#666] mt-1">/artist/{form.slug}</p>
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Email</Label>
            <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="edit-email" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Bio</Label>
            <Textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
              rows={3} className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="edit-bio" />
          </div>
          <div>
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Display Order</Label>
            <Input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none w-24" data-testid="edit-order" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {['spotify', 'apple_music', 'soundcloud', 'youtube_music', 'tidal', 'amazon_music', 'deezer', 'audiomack', 'bandcamp', 'pandora', 'instagram', 'twitter', 'youtube', 'tiktok', 'website'].map(platform => (
              <div key={platform}>
                <Label className="text-[10px] text-[#666] uppercase">{platform.replace('_', ' ')}</Label>
                <Input value={form.links[platform] || ''} onChange={e => setForm(p => ({ ...p, links: { ...p.links, [platform]: e.target.value } }))}
                  placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8"
                  data-testid={`edit-link-${platform}`} />
              </div>
            ))}
          </div>

          {/* 3 Video Slots */}
          <div data-testid="video-slots-section">
            <Label className="text-xs text-[#DAA520] font-bold uppercase">Video Links (up to 3)</Label>
            <p className="text-[10px] text-[#666] mt-0.5 mb-2">YouTube or Vimeo URLs — embedded on the artist&apos;s sub-page (separate from Spotify).</p>
            <div className="space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] text-[#525252] w-4">{i + 1}.</span>
                  <Input
                    value={form.videos[i] || ''}
                    onChange={e => setForm(p => {
                      const v = [...(p.videos || [])];
                      while (v.length <= i) v.push('');
                      v[i] = e.target.value;
                      return { ...p, videos: v };
                    })}
                    placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
                    className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8 font-mono"
                    data-testid={`video-slot-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Toggles: free downloads + preview mode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button type="button"
              onClick={() => setForm(p => ({ ...p, show_free_downloads: !p.show_free_downloads }))}
              className={`flex items-center justify-between gap-2 px-3 py-2 border transition-colors ${form.show_free_downloads ? 'border-[#22C55E] bg-[#22C55E]/10' : 'border-[#262626] bg-[#0A0A0A]'}`}
              data-testid="toggle-free-downloads">
              <span className="text-[10px] font-bold uppercase tracking-wider text-left">
                Free Downloads<br /><span className="text-[#666] font-normal normal-case">Show download buttons on sub-page</span>
              </span>
              <span className={`text-[10px] font-black uppercase ${form.show_free_downloads ? 'text-[#22C55E]' : 'text-[#666]'}`}>
                {form.show_free_downloads ? 'ON' : 'OFF'}
              </span>
            </button>
            <button type="button"
              onClick={() => setForm(p => ({ ...p, preview_mode: p.preview_mode === 'snippet' ? 'full' : 'snippet' }))}
              className={`flex items-center justify-between gap-2 px-3 py-2 border transition-colors ${form.preview_mode === 'snippet' ? 'border-[#DAA520] bg-[#DAA520]/10' : 'border-[#262626] bg-[#0A0A0A]'}`}
              data-testid="toggle-preview-mode">
              <span className="text-[10px] font-bold uppercase tracking-wider text-left">
                Preview Mode<br /><span className="text-[#666] font-normal normal-case">Sub-page playback default</span>
              </span>
              <span className={`text-[10px] font-black uppercase ${form.preview_mode === 'snippet' ? 'text-[#DAA520]' : 'text-[#22C55E]'}`}>
                {form.preview_mode === 'snippet' ? '30s SNIPPET' : 'FULL SONG'}
              </span>
            </button>
          </div>

          <Button onClick={handleSubmit} disabled={saving} className="w-full rounded-none bg-[#DAA520] text-black font-bold" data-testid="edit-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            SAVE CHANGES
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FeaturedArtists;
